import React, {useEffect, useState} from 'react';
import {Layout, Table} from "antd";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {Button, Card, Container, Input} from "@nextui-org/react";
import {MDBCol, MDBIcon, MDBRow} from "mdbreact";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, getFirestore, writeBatch, doc} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import {generatePath, useNavigate} from "react-router-dom";
import {Tag} from "primereact/tag";
import {useToast} from "@chakra-ui/react";
import {ColumnsType} from "antd/lib/table";


interface ProductRow {
    key: string;       // Firestore doc ID
    productId: string;
    name: string;
    seller: string;
    sellerId: string;
    category: string;
    price: string;
    stock: number;
    statusText: string;
    status: JSX.Element;
    onSale: boolean;
    img: string;
}

const STATUS_OPTIONS = ['Active', 'Inactive', 'Suspended'];

// A product is genuinely "on sale" only when isOnSale is backed by real sale
// data that lowers the price (a usable salePrice or a positive discountPercent).
// Mirrors the apps (Flutter DiscountHelper / webapp pricing.ts) so a stale
// `isOnSale: true` with no sale fields is not flagged.
function isReallyOnSale(d: any): boolean {
    if (d?.isOnSale !== true) return false;
    const saleType: string = d.saleType ?? '';
    const salePrice = parseFloat(String(d.salePrice ?? ''));
    const percent = Number(d.discountPercent ?? 0);
    if (saleType === 'fixed') return !isNaN(salePrice) && salePrice > 0;
    if (saleType === 'percent') return percent > 0;
    return (!isNaN(salePrice) && salePrice > 0) || percent > 0;
}

const { Content } = Layout;
const db = getFirestore(Firebase);

const Products = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [collapsed, setCollapsed] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
    const [allRows, setAllRows] = useState<ProductRow[]>([]);
    const [displayRows, setDisplayRows] = useState<ProductRow[]>([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [sellerFilter, setSellerFilter] = useState<string>('all');
    const [categories, setCategories] = useState<string[]>([]);
    const [sellers, setSellers] = useState<string[]>([]);
    const [updating, setUpdating] = useState(false);

    const [productSnapshot, loading] = useCollection(
        collection(db, 'products'),
        { snapshotListenOptions: { includeMetadataChanges: true } }
    );
    const [userSnapshot] = useCollection(
        collection(db, 'users'),
        { snapshotListenOptions: { includeMetadataChanges: true } }
    );

    function numberWithCommas(x: string | number) {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    useEffect(() => {
        if (!productSnapshot) return;

        // Build a UID → name lookup from users collection
        const uidToName: Record<string, string> = {};
        userSnapshot?.docs.forEach(d => {
            const u = d.data();
            const name = u.fullName ?? u.Fullname ?? '';
            if (name) uidToName[d.id] = name;
            if (u.Uid && name) uidToName[u.Uid] = name;
            if (u.sellerID && name) uidToName[u.sellerID] = name;
        });

        const rows: ProductRow[] = [];
        const catSet = new Set<string>();
        const sellerSet = new Set<string>();

        productSnapshot.docs.forEach(docSnap => {
            const d = docSnap.data();
            const statusText: string = d.productStatus ?? 'Unknown';
            const severity = statusText === 'Active' ? 'success'
                : statusText === 'Inactive' ? 'warning'
                : 'danger';
            const cat = d.category ?? '';
            const sellerName = uidToName[d.sellerId] ?? d.sellerName ?? d.sellerId ?? '';
            if (cat) catSet.add(cat);
            if (sellerName) sellerSet.add(sellerName);

            rows.push({
                key: docSnap.id,
                productId: d.productId ?? docSnap.id,
                name: d.name ?? '',
                seller: sellerName,
                sellerId: d.sellerId ?? '',
                category: cat,
                price: d.price ?? '0',
                stock: (d.stock as number) ?? parseInt(d.quantity ?? '0') ?? 0,
                statusText,
                status: <Tag severity={severity as any} value={statusText} />,
                onSale: isReallyOnSale(d),
                img: (d.images && d.images.length > 0) ? d.images[0] : '',
            });
        });

        setAllRows(rows);
        setCategories(Array.from(catSet).sort());
        setSellers(Array.from(sellerSet).sort());
    }, [productSnapshot]);

    useEffect(() => {
        let filtered = allRows;

        if (statusFilter !== 'all') {
            filtered = filtered.filter(r => r.statusText === statusFilter);
        }
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(r => r.category === categoryFilter);
        }
        if (sellerFilter !== 'all') {
            filtered = filtered.filter(r => r.seller === sellerFilter);
        }
        if (searchText.trim()) {
            const lower = searchText.toLowerCase();
            filtered = filtered.filter(r =>
                r.name.toLowerCase().includes(lower) ||
                r.seller.toLowerCase().includes(lower) ||
                r.category.toLowerCase().includes(lower)
            );
        }

        setDisplayRows(filtered);
        // Clear selection when filter changes
        setSelectedKeys([]);
    }, [allRows, statusFilter, categoryFilter, sellerFilter, searchText]);

    const handleBulkStatusChange = async (newStatus: string) => {
        if (selectedKeys.length === 0) return;
        setUpdating(true);
        try {
            const batch = writeBatch(db);
            selectedKeys.forEach(key => {
                batch.update(doc(db, 'products', key as string), { productStatus: newStatus });
            });
            await batch.commit();
            toast({
                position: 'top',
                title: `${selectedKeys.length} product(s) set to ${newStatus}`,
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            setSelectedKeys([]);
        } catch (e: any) {
            toast({ position: 'top', title: e.message, status: 'error', duration: 3000, isClosable: true });
        } finally {
            setUpdating(false);
        }
    };

    const rowSelection = {
        selectedRowKeys: selectedKeys,
        onChange: (keys: React.Key[]) => setSelectedKeys(keys),
    };

    const columns: ColumnsType<ProductRow> = [
        {
            title: '',
            dataIndex: 'img',
            key: 'img',
            width: 60,
            // The product tile opens that seller's products.
            render: (src: string, row: ProductRow) => {
                const go = () => row.sellerId && navigate(generatePath('/shops/:id', {id: row.sellerId}));
                const style: React.CSSProperties = {width: 44, height: 44, borderRadius: 6, cursor: row.sellerId ? 'pointer' : 'default'};
                return src
                    ? <img src={src} alt="" onClick={go} title="View seller's products" style={{...style, objectFit: 'cover'}} />
                    : <div onClick={go} style={{...style, background:'#f0f0f0'}} />;
            },
        },
        {
            title: 'Product',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, row: ProductRow) => (
                <a onClick={() => navigate(generatePath('/products/:id', {id: row.productId}))}
                   style={{cursor:'pointer', color:'#29327b', fontWeight:500}}>
                    {text}
                </a>
            ),
            sorter: (a: ProductRow, b: ProductRow) => a.name.localeCompare(b.name),
        },
        {
            title: 'Seller',
            dataIndex: 'seller',
            key: 'seller',
            render: (text: string, row: ProductRow) => row.sellerId
                ? <a onClick={() => navigate(generatePath('/shops/:id', {id: row.sellerId}))}
                     title="View seller's products"
                     style={{cursor:'pointer', color:'#29327b'}}>{text}</a>
                : text,
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
        },
        {
            title: 'Price (MWK)',
            dataIndex: 'price',
            key: 'price',
            render: (p: string) => numberWithCommas(p),
            sorter: (a: ProductRow, b: ProductRow) => parseFloat(a.price) - parseFloat(b.price),
        },
        {
            title: 'Promotion',
            dataIndex: 'onSale',
            key: 'onSale',
            width: 110,
            filters: [
                { text: 'On sale', value: true },
                { text: 'Not on sale', value: false },
            ],
            onFilter: (value, row: ProductRow) => row.onSale === value,
            render: (onSale: boolean) => onSale
                ? <Tag severity="success" value="On sale" />
                : <span style={{color:'#bbb'}}>—</span>,
        },
        {
            title: 'Stock',
            dataIndex: 'stock',
            key: 'stock',
            sorter: (a: ProductRow, b: ProductRow) => a.stock - b.stock,
            render: (n: number) => (
                <span style={{color: n === 0 ? '#c00' : n <= 3 ? '#e67e22' : 'inherit', fontWeight: n <= 3 ? 600 : 400}}>
                    {n}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
    ];

    const statusCounts = STATUS_OPTIONS.reduce((acc, s) => {
        acc[s] = allRows.filter(r => r.statusText === s).length;
        return acc;
    }, {} as Record<string, number>);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <Container className="mt-2">

                        {/* Header bar */}
                        <MDBRow>
                            <MDBCol>
                                <div className="page-header">
                                    <h4 className="page-title">
                                        Products
                                        <span className="page-title-count">
                                            ({displayRows.length}{displayRows.length !== allRows.length ? ` of ${allRows.length}` : ''})
                                        </span>
                                    </h4>
                                    <Input
                                        placeholder="search by name, seller, category"
                                        value={searchText}
                                        onChange={(e: any) => setSearchText(e.target.value)}
                                        style={{minWidth: 260}}
                                        contentRight={<MDBIcon icon="search"/>}
                                    />
                                </div>
                            </MDBCol>
                        </MDBRow>

                        {/* Filters */}
                        <MDBRow>
                            <MDBCol>
                                <div className="filter-bar">
                                    <span className="filter-label">Status:</span>
                                    {['all', ...STATUS_OPTIONS].map(s => (
                                        <button key={s}
                                            className={`filter-chip${statusFilter === s ? ' active' : ''}`}
                                            onClick={() => setStatusFilter(s)}>
                                            {s === 'all' ? `All (${allRows.length})` : `${s} (${statusCounts[s] ?? 0})`}
                                        </button>
                                    ))}

                                    {categories.length > 0 && (<>
                                        <span className="filter-label" style={{marginLeft:8}}>Category:</span>
                                        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                                            style={{fontSize:12, padding:'3px 8px', borderRadius:8, border:'1px solid #eee', color:'#444'}}>
                                            <option value="all">All</option>
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </>)}

                                    {sellers.length > 0 && (<>
                                        <span className="filter-label" style={{marginLeft:8}}>Seller:</span>
                                        <select value={sellerFilter} onChange={e => setSellerFilter(e.target.value)}
                                            style={{fontSize:12, padding:'3px 8px', borderRadius:8, border:'1px solid #eee', color:'#444'}}>
                                            <option value="all">All</option>
                                            {sellers.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </>)}
                                </div>
                            </MDBCol>
                        </MDBRow>

                        {/* Bulk actions bar — only shows when items are selected */}
                        {selectedKeys.length > 0 && (
                            <MDBRow>
                                <MDBCol>
                                    <div style={{
                                        background:'#f0f4ff', border:'1px solid #c7d2fe',
                                        borderRadius:10, padding:'10px 18px',
                                        display:'flex', alignItems:'center', flexWrap:'wrap', gap:10, marginBottom:8
                                    }}>
                                        <span style={{fontWeight:600, color:'#29327b'}}>
                                            {selectedKeys.length} product{selectedKeys.length > 1 ? 's' : ''} selected
                                        </span>
                                        <span style={{color:'#888', fontSize:13, marginLeft:4}}>— set status to:</span>
                                        {STATUS_OPTIONS.map(s => (
                                            <Button key={s} size="xs" auto
                                                    color={s === 'Active' ? 'success' : s === 'Inactive' ? 'warning' : 'error'}
                                                    disabled={updating}
                                                    onPress={() => handleBulkStatusChange(s)}>
                                                {s}
                                            </Button>
                                        ))}
                                        <Button size="xs" auto flat color="default"
                                                onPress={() => setSelectedKeys([])}>
                                            Clear selection
                                        </Button>
                                    </div>
                                </MDBCol>
                            </MDBRow>
                        )}

                        {/* Table */}
                        <MDBRow>
                            <MDBCol>
                                <Card className="my-1 font-JosefinSans overflow-hidden">
                                    <Table
                                        rowSelection={rowSelection}
                                        columns={columns}
                                        dataSource={displayRows}
                                        loading={loading || updating}
                                        rowKey="key"
                                        pagination={{ defaultPageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
                                        size="middle"
                                    />
                                </Card>
                            </MDBCol>
                        </MDBRow>

                    </Container>
                </Content>
            </Layout>
        </Layout>
    );
}

export default Products;
